import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AdminLoginScreen = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value?.trim()) return 'Admin email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex?.test(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Admin password is required';
        if (value?.length < 6) return 'Password must be at least 6 characters';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (successMessage) setSuccessMessage('');
    if (touched?.[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email?.trim()) {
      newErrors.email = 'Admin email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(formData?.email)) newErrors.email = 'Please enter a valid email address';
    }
    if (!formData?.password) {
      newErrors.password = 'Admin password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    setTouched({ email: true, password: true });
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const { user, userProfile } = await signIn(formData?.email, formData?.password, 'admin');
      if (userProfile?.role !== 'admin') {
        setErrors({ submit: 'Access denied. Admin credentials required.' });
        return;
      }
      setSuccessMessage('Admin login successful! Redirecting to console...');
      setTimeout(() => navigate('/admin-console'), 1000);
    } catch (error) {
      setErrors({ submit: error?.message || 'Invalid admin credentials' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoAdminLogin = async () => {
    setFormData({ email: 'demoadmin@mydeckaudit.com', password: 'DemoAdmin2026!' });
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const { user, userProfile } = await signIn('demoadmin@mydeckaudit.com', 'DemoAdmin2026!', 'admin');
      if (userProfile?.role !== 'admin') {
        setErrors({ submit: 'Access denied. Admin credentials required.' });
        return;
      }
      setSuccessMessage('Admin login successful! Redirecting to console...');
      setTimeout(() => navigate('/admin-console'), 1000);
    } catch (error) {
      setErrors({ submit: error?.message || 'Demo admin login failed' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/20 border-2 border-amber-500/30 mb-4">
            <Icon name="ShieldCheck" className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Admin Access
          </h1>
          <p className="text-base text-slate-300">
            Secure authentication for myDeckAudit administrators
          </p>
        </div>

        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl shadow-2xl p-6 md:p-8">

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle2" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-300 font-medium">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {errors?.submit && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{errors?.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Admin Email */}
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">
                Admin Email <span className="text-red-400">*</span>
              </label>
              <Input
                type="email"
                name="email"
                placeholder="admin@mydeckaudit.com"
                value={formData?.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched?.email ? errors?.email : ''}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            {/* Admin Password */}
            <div className="relative">
              <label className="block text-sm font-medium text-white mb-1.5">
                Admin Password <span className="text-red-400">*</span>
              </label>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Enter admin password"
                value={formData?.password}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched?.password ? errors?.password : ''}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-slate-400 hover:text-white transition-colors"
                disabled={isLoading}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} className="w-5 h-5" />
              </button>
            </div>

            {/* Forgot Password */}
            <div className="flex items-center justify-end">
              <a
                href="#forgot-admin-password"
                className="text-sm text-amber-400 hover:underline font-medium"
              >
                Forgot Admin Password?
              </a>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              iconName="ShieldCheck"
              iconPosition="right"
              className="mt-6 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              Admin Sign In
            </Button>
          </form>

          {/* Enhanced Security Features */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
              <div className="flex items-start gap-3">
                <Icon name="Info" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white mb-1">Enhanced Security Features</p>
                  <ul className="text-xs text-slate-300 space-y-1">
                    <li>• Role verification on every login</li>
                    <li>• Audit logging for all access attempts</li>
                    <li>• Session timeout protection</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Demo Admin Login */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <p className="text-xs text-center text-slate-400 mb-3">
              🔒 Demo Admin Credentials for Testing
            </p>
            <button
              type="button"
              onClick={handleDemoAdminLogin}
              disabled={isLoading}
              className="w-full text-left px-4 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-sm transition-colors disabled:opacity-50 border border-slate-600"
            >
              <span className="font-medium text-amber-400">Admin:</span>{' '}
              <span className="text-slate-300">demoadmin@mydeckaudit.com / DemoAdmin2026!</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
            <Icon name="Shield" className="w-4 h-4" />
            <span>Administrative access only • Unauthorized access prohibited</span>
          </div>
          <div className="mt-3">
            <Link
              to="/login-screen"
              className="text-sm text-amber-400 hover:underline font-medium"
            >
              ← Back to Customer Login
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminLoginScreen;